import { DishResponse, MenuSectionHeader } from "api-types";

/**
 * A pre-nested view of a menu section's rows. Nesting is expressed by
 * `depth` + `children` rather than by walking a flat display-order list —
 * building this tree from the API's flat itemsInDisplayOrder/indentsByItemId
 * shape is a separate concern, not part of this component set.
 */
export type MenuTreeNode = {
  /** Unique section occurrence, not a reusable dish/header identifier. */
  id: string;
  /** Original dish/header id; used only to understand incomplete responses. */
  sourceId: string;
  kind: "dish" | "header";
  depth: number;
  dish?: DishResponse;
  header?: MenuSectionHeader;
  children: MenuTreeNode[];
};

/**
 * A header record with a usable title and a real required/optional value.
 * Shared by the adapter (deciding whether an empty group is safe to drop)
 * and the suitability layer (deciding whether a non-empty group's header is
 * too broken to summarise) so both draw the same line between "malformed"
 * and "empty". A header failing this check must keep failing closed, never
 * be silently dropped.
 */
export const isValidGuestGroupHeader = (
  header: MenuSectionHeader | undefined
): header is MenuSectionHeader =>
  !!header &&
  typeof header.title === "string" &&
  header.title.trim().length > 0 &&
  typeof header.required === "boolean";

/** The fields every node-rendering surface (row, drawer) reads off a node,
 * regardless of whether it turns out to be a dish or a header. */
export const nodeDisplayInfo = (node: MenuTreeNode) => ({
  title: node.kind === "dish" ? node.dish?.name : node.header?.title,
  description:
    node.kind === "dish" ? node.dish?.description : node.header?.description,
  imageUrl: node.kind === "dish" ? node.dish?.imageUrl : undefined,
});
